import { currencySchema, type Currency } from '@bonboca/shared-schemas';
import { z } from 'zod';

export const CART_STORAGE_KEY = 'bb_cart';
export const CART_TTL_MILLISECONDS = 6 * 60 * 60 * 1000;

export const cartItemSchema = z.object({
  menuItemId: z.string().min(1),
  name: z.string().min(1),
  unitPriceMinorUnits: z.string().regex(/^(0|[1-9]\d*)$/),
  quantity: z.number().int().min(1).max(99),
});
export type CartItem = z.infer<typeof cartItemSchema>;

export const cartStateSchema = z.object({
  restaurantId: z.string().min(1),
  restaurantSlug: z.string().min(1),
  restaurantName: z.string().min(1),
  currency: currencySchema,
  items: z.array(cartItemSchema),
  updatedAt: z.number().int().nonnegative(),
  acceptsPickup: z.boolean().default(true),
});
export type CartState = z.infer<typeof cartStateSchema>;

export type CartAction =
  | {
      type: 'ADD_ITEM';
      restaurantId: string;
      restaurantSlug: string;
      restaurantName: string;
      currency: Currency;
      acceptsPickup: boolean;
      item: Omit<CartItem, 'quantity'> & { quantity?: number };
    }
  | { type: 'SET_QUANTITY'; menuItemId: string; quantity: number }
  | { type: 'REMOVE_ITEM'; menuItemId: string }
  | { type: 'CLEAR' }
  | { type: 'HYDRATE'; state: CartState | null };

export function isCartExpired(state: CartState, now: number): boolean {
  return now - state.updatedAt > CART_TTL_MILLISECONDS;
}

export function cartReducer(
  state: CartState | null,
  action: CartAction,
  now: number = Date.now(),
): CartState | null {
  switch (action.type) {
    case 'HYDRATE':
      return action.state;

    case 'ADD_ITEM': {
      const incoming = { ...action.item, quantity: action.item.quantity ?? 1 } satisfies CartItem;

      if (state === null || state.restaurantId !== action.restaurantId) {
        return {
          restaurantId: action.restaurantId,
          restaurantSlug: action.restaurantSlug,
          restaurantName: action.restaurantName,
          currency: action.currency,
          acceptsPickup: action.acceptsPickup,
          items: [incoming],
          updatedAt: now,
        };
      }

      const existing = state.items.find((entry) => entry.menuItemId === incoming.menuItemId);
      const nextItems = existing
        ? state.items.map((entry) =>
            entry.menuItemId === incoming.menuItemId
              ? { ...entry, quantity: Math.min(99, entry.quantity + incoming.quantity) }
              : entry,
          )
        : [...state.items, incoming];
      return { ...state, items: nextItems, updatedAt: now };
    }

    case 'SET_QUANTITY': {
      if (state === null) return state;
      if (action.quantity <= 0) {
        const items = state.items.filter((entry) => entry.menuItemId !== action.menuItemId);
        if (items.length === 0) return null;
        return { ...state, items, updatedAt: now };
      }
      const items = state.items.map((entry) =>
        entry.menuItemId === action.menuItemId
          ? { ...entry, quantity: Math.min(99, action.quantity) }
          : entry,
      );
      return { ...state, items, updatedAt: now };
    }

    case 'REMOVE_ITEM': {
      if (state === null) return state;
      const items = state.items.filter((entry) => entry.menuItemId !== action.menuItemId);
      if (items.length === 0) return null;
      return { ...state, items, updatedAt: now };
    }

    case 'CLEAR':
      return null;

    default:
      return state;
  }
}
